import { DOCUMENT } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { getIconSprite } from './icon-sprite';

function parseIconSize(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 24;
  }
  if (typeof value === 'number') {
    return isNaN(value) ? 24 : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === 'xs') return 12;
    if (trimmed === 'sm') return 16;
    if (trimmed === 'md') return 24;
    if (trimmed === 'lg') return 32;
    if (trimmed === 'xl') return 48;
    if (trimmed.endsWith('px')) {
      const parsed = parseFloat(trimmed);
      return isNaN(parsed) ? 24 : parsed;
    }
    const num = Number(trimmed);
    return isNaN(num) ? 24 : num;
  }
  return 24;
}

export type IconMode = 'svg' | 'font';

@Component({
  selector: 'hx-icon',
  standalone: true,
  template: `
    @if (_mode() === 'svg') {
      <svg [attr.fill]="'currentColor'" [attr.focusable]="false" [attr.height]="_svgSize()" [attr.width]="_svgSize()">
        <use [attr.href]="_spritePath()" />
      </svg>
    }
  `,
  styleUrls: ['./icon.css'],
  host: {
    role: 'img',
    class: 'hx-icon',
    '[class.hx-icon-inline]': 'inline()',
    '[class]': '"hx-icon " + (inline() ? "hx-icon-inline " : "") + _fontClasses()',
    '[attr.data-icon-type]': '_mode()',
    '[attr.aria-hidden]': '_ariaHidden()',
    '[style.width]': '!inline() ? _hostSize() : null',
    '[style.height]': '!inline() ? _hostSize() : null',
    '[style.font-size]': '!inline() ? _hostSize() : null',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'hxIcon',
})
export class HxIcon extends HxBaseControl {
  private _elementRef = inject(ElementRef<HTMLElement>);
  private readonly sprite = getIconSprite(inject(DOCUMENT));

  constructor() {
    super();
    effect(() => {
      if (this.svgIcon()) this.sprite.load();
    });
  }

  // Inputs
  readonly svgIcon = input<string | null>(null);
  readonly fontIcon = input<string | null>(null);
  readonly fontSet = input<string>('ccs'); // Default to Icons
  readonly inline = input(false, { transform: booleanAttribute });

  /** When true, appends "-fill" suffix to the SVG symbol ID to render the filled variant. */
  readonly filled = input(false, { transform: booleanAttribute });

  /** Size of the icon in px (e.g. 16 = 16px). Defaults to 24. Ignored when `inline` is true. */
  readonly size = input(24, { transform: (value: unknown) => parseIconSize(value) });

  // Computed properties
  protected readonly _mode = computed<IconMode>(() => {
    return this.svgIcon() ? 'svg' : 'font';
  });

  protected readonly _fontClasses = computed(() => {
    if (this._mode() !== 'font') {
      return '';
    }
    const fontSet = this.fontSet();
    const fontIcon = this.fontIcon();
    const classes: string[] = [];
    if (fontSet) {
      classes.push(fontSet);
    }
    if (fontIcon) {
      const iconClass = fontIcon.startsWith('ccs-') ? fontIcon : `ccs-${fontIcon}`;
      classes.push(iconClass);
    }
    return classes.join(' ');
  });

  protected readonly _spritePath = computed(() => {
    const icon = this.svgIcon();
    if (!icon) {
      return '';
    }

    // Support namespace syntax: "namespace:icon-name"
    const [, iconName] = this._splitIconName(icon);
    let name = iconName || icon;

    if (this.filled()) {
      name = `${name}-fill`;
    }

    // Always use ccs- prefix for sprite symbols
    const symbolId = name.startsWith('ccs-') ? name : `ccs-${name}`;
    const status = this.sprite.status();
    if (status === 'ready') return `#${symbolId}`;
    return status === 'failed' ? `/assets/icons/complexcare-icons.svg#${symbolId}` : null;
  });

  protected readonly _hostSize = computed(() => {
    const s = this.size();
    const safeSize = isNaN(s) ? 24 : s;
    return `${safeSize}px`;
  });

  protected readonly _svgSize = computed(() => {
    return this.inline() ? '1em' : this._hostSize();
  });

  protected readonly _ariaHidden = computed(() => {
    // Only hide from screen readers if no explicit aria-label
    const hasLabel = this._elementRef.nativeElement.hasAttribute('aria-label');
    return hasLabel ? null : 'true';
  });

  private _splitIconName(iconName: string): [string, string] {
    if (!iconName) {
      return ['', ''];
    }
    const parts = iconName.split(':');
    if (parts.length === 2) {
      return [parts[0] || '', parts[1] || ''];
    }
    return ['', iconName];
  }
}
